import React, { useEffect, useState, useRef } from "react";
import {
  GoogleMap,
  Marker,
  Polygon,
  LoadScript,
  InfoWindow,
  OverlayView
} from "@react-google-maps/api";
import { GOOGLE_MAP_KEY } from "../../../utils/Config";
import "../chmActivities/CHMActivities.css";
import * as geolib from "geolib";
import arrowIcon from "../../../assets/img/IMG_navigation@2x.svg";

const mapContainerStyle = {
  width: "100%",
  height: "368px",
  borderRadius: "12px",
  overflow: "hidden",
};

const defaultCords = {
  lat: 20.5937,
  lng: 78.9629,
};
interface MapData {
  lat: number;
  lng: number;
  polygonCentroid: string;
  polygon: string;
  surveyNumber?: string;
  cropName?: string;
  villageName?: string;
  imageCoordinates?: any;
}

interface CHMActivityMapProps {
  mapData: MapData;
}

const getImageMarkers = (imageCoordinates: any) => {
  if (!imageCoordinates) return [];

  const markers: { lat: number; lng: number; rotation: number; key: number }[] = [];

  Object.entries(imageCoordinates).forEach(([name, coords]) => {
    if (Array.isArray(coords) && coords.length === 3) {
      const index = name == "eastImageCoordinates" ? 0 : name == "westImageCoordinates" ? 1 : name == "northImageCoordinates"  ? 3 : 2;
      markers.push({ lat: Number(coords[0]), lng: Number(coords[1]), rotation: Number(coords[2]), key: index, });
    }
  });

  return markers;
};

const CHMActivityMap: React.FC<CHMActivityMapProps> = ({ mapData }) => {
  const [cords, setCords] = useState<any>({});
  const [unqPolygon, setUnqPolygon] = useState<any>([]);
  const [infoWindow, setInfoWindow] = useState(false);
  const [isValid, setIsValid] = useState(false);
  const [imageCoords, setImageCoords] = useState<any>({});
  const mapRef = useRef<google.maps.Map | null>(null);

  useEffect(() => {
    if (mapData?.polygonCentroid) {
      setCords({
        lat: Number(mapData.polygonCentroid.split(" ")[1]?.replace(")", "")),
        lng: Number(mapData.polygonCentroid.split(" ")[0]?.replace("POINT(", "")),
      });
    }

    if (mapData?.polygon) {
      const polygonPoints = mapData.polygon
        .replace("POLYGON((", "")
        .replace("))", "")
        .split(",")
        .map((point) => {
          const [lng, lat] = point.trim().split(" ");
          return { lat: Number(lat), lng: Number(lng) };
        });
      setUnqPolygon(polygonPoints);
    }
  }, [mapData]);

  useEffect(() => {
    distanceCalculate();
  }, [unqPolygon, cords]);

  const distanceCalculate = () => {
    if (!mapData?.lat || !mapData?.lng || unqPolygon.length === 0) return;
    const distances = unqPolygon.map((vertex: any) =>
      geolib.getDistance({ lat: mapData.lat, lng: mapData.lng }, vertex)
    );
    const minDistance = Math.min(...distances);
    setIsValid(minDistance <= 30);
  };

  const handleMapLoad = (map: google.maps.Map) => {
    mapRef.current = map;
    if (unqPolygon.length > 0) {
      const bounds = new google.maps.LatLngBounds();
      unqPolygon.forEach((point: any) => bounds.extend(point));
      map.fitBounds(bounds); // auto zoom + center
    }
  };

  return (
    <LoadScript googleMapsApiKey={GOOGLE_MAP_KEY}>
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        zoom={cords.lat && cords.lng ? 16 : 4}
        center={cords.lat && cords.lng ? cords : defaultCords}
        onLoad={handleMapLoad}
        options={{
          mapTypeId: "hybrid",
          zoomControl: true,
          mapTypeControl: true,
          scaleControl: true,
          streetViewControl: false,
          rotateControl: false,
          fullscreenControl: true,
          gestureHandling: "panning",
        }}
      >
        {/* Marker only if centroid exists */}
        {cords.lat && cords.lng && (
          <Marker
            position={cords}
            icon={{
              url: isValid
                ? require("../../../assets/img/marker.svg").default
                : require("../../../assets/img/marker_red.svg").default,
            }}
            onMouseOver={() => setInfoWindow(true)}
            onMouseOut={() => setInfoWindow(false)}
          >
            {infoWindow && (
              <InfoWindow position={cords} onCloseClick={() => setInfoWindow(false)}>
                <div style={{ background: "#0D2316", height: "87px", width: "157px", padding: 8 }}>
                  {mapData.surveyNumber && <p style={{ color: "#fff" }}>Survey: {mapData.surveyNumber}</p>}
                  {mapData.cropName && <p style={{ color: "#fff" }}>Crop: {mapData.cropName}</p>}
                  {mapData.villageName && <p style={{ color: "#fff" }}>Village: {mapData.villageName}</p>}
                </div>
              </InfoWindow>
            )}
          </Marker>
        )}

        {unqPolygon.length > 0 && (
          <Polygon
            paths={unqPolygon}
            options={{
              fillColor: "#61322f",
              fillOpacity: 0.9,
              strokeColor: "black",
              strokeWeight: 1,
            }}
          />
        )}

        {mapData?.imageCoordinates &&
          getImageMarkers(mapData.imageCoordinates).map((marker) => (
            <OverlayView
              key={marker.key}
              position={{ lat: marker.lat, lng: marker.lng }}
              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
            >
              <div style={{ position: "relative", width: 40, height: 40 }}>
                <img
                  src={arrowIcon}
                  alt="arrow"
                  style={{
                    width: 40,
                    height: 40,
                    transform: `rotate(${marker.rotation}deg) translateY(-10px)`,
                    transformOrigin: "50% 50%",
                  }}
                />
                {/* Circle overlay, centered */}
                <div
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: 20,
                    height: 20,
                    borderRadius: "50%",
                    backgroundColor: "black",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <span style={{ color: "white", fontSize: 12 }}>{marker.key + 1}</span>
                </div>
              </div>
            </OverlayView>
          ))}
      </GoogleMap>
    </LoadScript >
  );
};


export default CHMActivityMap;
